import { useEffect, useState } from "react";
import { api, setToken, type Me } from "../lib/api.js";
import { Notice } from "../components/ui.js";

interface LoginResponse {
  access_token?: string;
  type?: string;
  tfa_type?: string;
  secret?: string;
  user?: { name: string };
}

export default function Login({ onSignedIn }: { onSignedIn: (me: Me) => void }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [secret, setSecret] = useState("");
  const [stage, setStage] = useState<"password" | "twofactor">("password");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [providers, setProviders] = useState<Array<{ name: string; display_name?: string }>>([]);

  useEffect(() => {
    // Third-party sign-in options are advertised by the same endpoint the
    // desktop client uses, so both stay in step.
    fetch("/api/login-options")
      .then((r) => (r.ok ? r.json() : []))
      .then((opts: string[]) => {
        const common = opts.find((o) => o.startsWith("common-oidc/"));
        if (common) setProviders(JSON.parse(common.slice("common-oidc/".length)));
      })
      .catch(() => setProviders([]));
  }, []);

  async function finish(token: string) {
    setToken(token);
    const me = await api.get<Me>("/api/console/me");
    onSignedIn(me);
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const body =
        stage === "password"
          ? { username, password, type: "account", id: "console", uuid: "console" }
          : { username, type: "email_code", secret, tfaCode: code, verificationCode: code,
              id: "console", uuid: "console" };
      const res = await api.post<LoginResponse>("/api/login", body);

      if (res.type === "access_token" && res.access_token) {
        await finish(res.access_token);
        return;
      }
      if (res.type === "email_check") {
        setSecret(res.secret ?? "");
        setStage("twofactor");
        setCode("");
        return;
      }
      setError("The server sent a response this console does not understand.");
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="login">
      <div className="login__card">
        <div className="login__brand">
          <span className="rail__mark">RD</span>
          <div>
            <div className="rail__name">RustDesk Console</div>
            <div className="rail__sub">Self-hosted</div>
          </div>
        </div>

        <div className="panel">
          <div className="panel__body">
            <form onSubmit={submit}>
              {stage === "password" ? (
                <>
                  <div className="field">
                    <label className="field__label" htmlFor="u">Username</label>
                    <input
                      id="u"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      autoComplete="username"
                      autoFocus
                      required
                    />
                  </div>
                  <div className="field">
                    <label className="field__label" htmlFor="p">Password</label>
                    <input
                      id="p"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      autoComplete="current-password"
                      required
                    />
                  </div>
                </>
              ) : (
                <div className="field">
                  <label className="field__label" htmlFor="c">Authentication code</label>
                  <input
                    id="c"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    placeholder="000000"
                    className="mono"
                    autoFocus
                    required
                  />
                  <span className="field__hint">
                    Enter the 6-digit code from your authenticator app.
                  </span>
                </div>
              )}

              {error ? <div style={{ marginBottom: "0.875rem" }}><Notice>{error}</Notice></div> : null}

              <button className="btn btn--primary" style={{ width: "100%", justifyContent: "center" }} disabled={busy}>
                {busy ? "Signing in…" : stage === "password" ? "Sign in" : "Verify"}
              </button>

              {stage === "twofactor" ? (
                <button
                  type="button"
                  className="btn btn--ghost"
                  style={{ width: "100%", justifyContent: "center", marginTop: "0.5rem" }}
                  onClick={() => { setStage("password"); setError(""); }}
                >
                  Use a different account
                </button>
              ) : null}
            </form>

            {providers.length && stage === "password" ? (
              <div style={{ marginTop: "1rem", paddingTop: "1rem", borderTop: "1px solid var(--line-soft)" }}>
                <div className="small faint" style={{ marginBottom: "0.5rem" }}>
                  Or continue with
                </div>
                <div className="row row--wrap">
                  {providers.map((p) => (
                    <a
                      key={p.name}
                      className="btn"
                      href={`/api/oidc/start/${encodeURIComponent(p.name)}`}
                    >
                      {p.display_name || p.name}
                    </a>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
